// Copyright 2026. WebPros International GmbH. All rights reserved.

import {
    DisasterRecoveryEntityStatus as EntityResponseStatus,
    IDisasterRecoveryEntityResponse,
} from 'common/api/resources/DisasterRecovery';
import React from 'react';
import {
    Action,
    Button,
    Translate,
} from '@plesk/ui-library';
import { Status } from 'common/components/styles/Status';
import { INTENT_TYPE } from 'common/constants';
import { dataCySelector } from 'common/tests/selectors';
import { DISASTER_RECOVERY_HISTORY } from 'admin/disasterRecovery/constants/test';

export interface IDisasterRecoveryEntityStatusProps {
    entity: IDisasterRecoveryEntityResponse;
    status: string;
    compact?: boolean;
    onTaskModalOpen: () => void;
}

export const statusMappings: Record<EntityResponseStatus, string> = {
    [EntityResponseStatus.PENDING]: 'disasterRecovery.history.status.pending',
    [EntityResponseStatus.RUNNING]: 'disasterRecovery.history.status.running',
    [EntityResponseStatus.COMPLETED]: 'disasterRecovery.history.status.completed',
    [EntityResponseStatus.FAILED]: 'disasterRecovery.history.status.failed',
};

const intentMappings: Partial<Record<EntityResponseStatus, INTENT_TYPE>> = {
    [EntityResponseStatus.PENDING]: INTENT_TYPE.WARNING,
    [EntityResponseStatus.RUNNING]: INTENT_TYPE.PRIMARY,
    [EntityResponseStatus.COMPLETED]: INTENT_TYPE.SUCCESS,
    [EntityResponseStatus.FAILED]: INTENT_TYPE.DANGER,
};

const STATUS_WITH_BUTTONS = [
    EntityResponseStatus.FAILED,
    EntityResponseStatus.COMPLETED,
];

export const DisasterRecoveryEntityStatus: React.FC<IDisasterRecoveryEntityStatusProps> = ({
    status,
    compact,
    onTaskModalOpen,
    entity,
}) => {
    const buttonEl = (
        <Button
            data-cy={dataCySelector(entity.id, DISASTER_RECOVERY_HISTORY.ENTITY_STATUS_MODAL)}
            onClick={onTaskModalOpen}
            ghost={true}
            icon="box-diagonal-top-in"
        />
    );

    if (entity.status === EntityResponseStatus.RUNNING) {
        return (
            <>
                <Action progress={true}>
                    <Translate content={statusMappings[entity.status]} /> {entity.task?.progress ? `${entity.task.progress}%` : ''}
                </Action>
                {buttonEl}
            </>
        );
    }

    const showButton = STATUS_WITH_BUTTONS.includes(entity.status);

    return (
        <>
            <Status
                intent={intentMappings[status]}
                compact={compact}
            >
                <Translate content={statusMappings[status]} />
            </Status>
            {showButton && buttonEl}
        </>
    );
};
